import { useState } from 'react';
import { auth } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminLayout from '../../components/admin/AdminLayout';
import { PRODUCT_CATEGORIES, PRODUCT_SIZES } from '../../utils/helpers';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';
import { uploadImage, deleteFile, generateFilename, getStoragePath, isStorageUrl } from '../../services/storageService';

function ManageShop() {
  const [user] = useAuthState(auth);
  const { club, clubId } = useClub();
  
  // Use tenant-scoped data hooks
  const { data: products, loading, setData: setProducts } = useTenantCollection('products');
  const { add, update, remove } = useTenantCrud('products');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: 'Jerseys',
    price: '',
    image: '',
    sizes: [],
    description: '',
    inStock: true
  });

  const categories = PRODUCT_CATEGORIES;
  const allSizes = PRODUCT_SIZES;

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Jerseys',
      price: '',
      image: '',
      sizes: [],
      description: '',
      inStock: true
    });
    setEditingProduct(null);
    setImagePreview('');
    setImageFile(null);
    setError('');
    setSuccess('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      category: product.category || 'Jerseys',
      price: product.price || '',
      image: product.image || '',
      sizes: product.sizes || [],
      description: product.description || '',
      inStock: product.inStock !== false
    });
    setImagePreview(product.image || '');
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSizeToggle = (size) => {
    if (formData.sizes.includes(size)) {
      setFormData({...formData, sizes: formData.sizes.filter(s => s !== size)});
    } else {
      setFormData({...formData, sizes: [...formData.sizes, size]});
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Store file for upload on save, show preview
    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    // Validation
    if (!formData.name || !formData.price) {
      setError('Please fill in product name and price');
      setSaving(false);
      return;
    }

    if (formData.sizes.length === 0) {
      setError('Please select at least one size');
      setSaving(false);
      return;
    }

    try {
      let imageUrl = formData.image;

      // Upload new image if selected
      if (imageFile) {
        const filename = generateFilename(imageFile.name);
        const productId = editingProduct?.id || `new-${Date.now()}`;
        const path = `${getStoragePath.productImage(clubId, productId)}/${filename}`;
        imageUrl = await uploadImage(imageFile, path);

        // Delete old image if it was in storage
        if (editingProduct?.image && isStorageUrl(editingProduct.image)) {
          try {
            await deleteFile(editingProduct.image);
          } catch (err) {
            console.warn('Could not delete old image:', err);
          }
        }
      }

      const productData = {
        name: formData.name,
        category: formData.category,
        price: parseInt(formData.price),
        image: imageUrl,
        sizes: formData.sizes,
        description: formData.description,
        inStock: formData.inStock
      };

      if (editingProduct) {
        // Update existing product (tenant-scoped)
        const updated = await update(editingProduct.id, productData);
        setProducts(products.map(p => 
          p.id === editingProduct.id ? updated : p
        ));
        setSuccess('Product updated successfully!');
      } else {
        // Create new product (tenant-scoped)
        const newProduct = await add(productData);
        setProducts([...products, newProduct]);
        setSuccess('Product added successfully!');
      }

      setTimeout(() => {
        setShowModal(false);
        resetForm();
      }, 1500);

    } catch (error) {
      console.error('Error saving product:', error);
      setError('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"?`)) return;

    try {
      // Find product to get image URL
      const product = products.find(p => p.id === productId);
      
      // Delete image from storage if it exists
      if (product?.image && isStorageUrl(product.image)) {
        try {
          await deleteFile(product.image);
        } catch (err) {
          console.warn('Could not delete product image:', err);
        }
      }
      
      // Delete product (tenant-scoped)
      await remove(productId);
      setProducts(products.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const toggleStock = async (product) => {
    try {
      const newStockStatus = !product.inStock;
      // Update stock status (tenant-scoped)
      await update(product.id, { inStock: newStockStatus });
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, inStock: newStockStatus } : p
      ));
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  // Filter products
  const filteredProducts = filterCategory === 'all' 
    ? products 
    : products.filter(p => p.category === filterCategory);

  // Stats
  const totalProducts = products.length;
  const inStockCount = products.filter(p => p.inStock !== false).length;
  const outOfStockCount = products.filter(p => p.inStock === false).length;

  return (
    <AdminLayout title="Manage Kit Shop" subtitle={`Add and manage products for ${club?.name || 'your club'}'s Kit Shop`}>
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={openAddModal}>
          + Add Product
        </button>
      </div>

          {/* Stats */}
          <div className="shop-stats">
            <div className="shop-stat-card">
              <span className="stat-number">{totalProducts}</span>
              <span className="stat-label">Total Products</span>
            </div>
            <div className="shop-stat-card">
              <span className="stat-number in-stock">{inStockCount}</span>
              <span className="stat-label">In Stock</span>
            </div>
            <div className="shop-stat-card">
              <span className="stat-number out-of-stock">{outOfStockCount}</span>
              <span className="stat-label">Out of Stock</span>
            </div>
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="filter-group">
              <label>Category:</label>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="admin-card">
            <div className="card-content">
              {loading ? (
                <p className="loading-text">Loading products...</p>
              ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">🛒</span>
                  <h3>No Products Yet</h3>
                  <p>Add your first product to the Kit Shop.</p>
                  <button className="btn-add" onClick={openAddModal}>
                    + Add Product
                  </button>
                </div>
              ) : (
                <div className="shop-products-grid">
                  {filteredProducts.map(product => (
                    <div key={product.id} className={`shop-product-card ${!product.inStock ? 'out-of-stock' : ''}`}>
                      <div className="product-image-container">
                        {product.image ? (
                          <img src={product.image} alt={product.name} className="product-thumb" />
                        ) : (
                          <div className="product-placeholder">🏷️</div>
                        )}
                        <span className="product-category-tag">{product.category}</span>
                        {!product.inStock && <span className="out-of-stock-badge">Out of Stock</span>}
                      </div>
                      <div className="product-details">
                        <h3>{product.name}</h3>
                        <p className="product-price">TT$ {product.price?.toLocaleString()}</p>
                        <p className="product-sizes">
                          {product.sizes?.join(', ') || 'No sizes'}
                        </p>
                      </div>
                      <div className="product-actions">
                        <button 
                          className={`btn-stock ${product.inStock ? 'in-stock' : 'restock'}`}
                          onClick={() => toggleStock(product)}
                        >
                          {product.inStock ? 'Mark Out of Stock' : 'Restock'}
                        </button>
                        <button 
                          className="btn-edit"
                          onClick={() => openEditModal(product)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(product.id, product.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label>Product Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Home Jersey 2026"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price (TT$) *</label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={e => setFormData({...formData, price: e.target.value})}
                      placeholder="350"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Stock Status</label>
                    <select
                      value={formData.inStock ? 'true' : 'false'}
                      onChange={e => setFormData({...formData, inStock: e.target.value === 'true'})}
                    >
                      <option value="true">In Stock</option>
                      <option value="false">Out of Stock</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Available Sizes *</label>
                  <div className="size-checkboxes">
                    {allSizes.map(size => (
                      <label key={size} className={`size-checkbox ${formData.sizes.includes(size) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData.sizes.includes(size)}
                          onChange={() => handleSizeToggle(size)}
                        />
                        {size}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Product description..."
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Product Image</label>
                  <div className="image-upload-section">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      id="product-image"
                      className="file-input"
                    />
                    <label htmlFor="product-image" className="file-upload-area">
                      {imagePreview ? (
                        <div className="image-preview-inline">
                          <img src={imagePreview} alt="Preview" />
                          <span className="change-photo">Click to change</span>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <span className="upload-icon">📷</span>
                          <span>Click to upload image</span>
                          <small>Max 500KB</small>
                        </div>
                      )}
                    </label>
                  </div>
                  <div className="form-group" style={{marginTop: '10px'}}>
                    <label>Or paste image URL:</label>
                    <input
                      type="url"
                      value={formData.image && !formData.image.startsWith('data:') ? formData.image : ''}
                      onChange={e => {
                        setFormData({...formData, image: e.target.value});
                        setImagePreview(e.target.value);
                      }}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageShop;
